import { POLL_ERROR_LABEL_META_KEY } from '@gonfalon/rest-api/pollErrorLabel';
import { trackError } from '@gonfalon/telemetry/trackError';

type PolledQuery = {
  meta?: Record<string, unknown>;
  state: { dataUpdateCount: number };
};

/**
 * `dataUpdateCount` at the time each query last had a failure reported. Keyed by the query itself,
 * so entries are collected when it leaves the cache.
 */
const reportedAtDataUpdateCount = new WeakMap<PolledQuery, number>();

/**
 * Reports a failing poll set up by `pollingQueryOptions`, once per outage.
 *
 * Polls run for minutes, so reporting every attempt would emit telemetry every few seconds for a
 * single outage. `dataUpdateCount` only advances on a success, so a failure arriving with the same
 * count as the last one we reported belongs to the same unbroken run and stays quiet.
 *
 * Note `fetchFailureCount` looks like the obvious signal and is not: `fetchState` resets it to 0 at
 * the start of every fetch, so with `retry: false` it is 1 on every failed poll.
 *
 * Lives at the cache rather than in the polling hook so one failure is reported once, not once per
 * component observing the same query.
 */
export function reportPollError(error: Error, query: PolledQuery) {
  const label = query.meta?.[POLL_ERROR_LABEL_META_KEY];

  if (typeof label !== 'string') {
    return false;
  }

  const { dataUpdateCount } = query.state;
  if (reportedAtDataUpdateCount.get(query) === dataUpdateCount) {
    return false;
  }

  reportedAtDataUpdateCount.set(query, dataUpdateCount);
  trackError(new Error(`${label}: ${JSON.stringify(error)}`), { severity: 'low', owner: 'team-foundation' });
  return true;
}
